import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatCurrency, formatDate, formatDateLong } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, FileText, Receipt, Download, TrendingUp, Package, Truck, Banknote, Clock, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { toast } from "sonner";

const PIE_COLORS = ["#E63946", "#424242", "#3B82F6", "#F59E0B", "#10B981", "#8B5CF6", "#EC4899", "#6B7280"];

export default function ReportsPage() {
  const [tab, setTab] = useState("quotes");
  const { user } = useAuth();

  if (!["admin", "sales_manager"].includes(user?.role)) {
    return <div className="text-center py-12 text-gray-400"><p className="font-medium">Access Denied</p><p className="text-sm mt-1">Only admins and sales managers can view reports</p></div>;
  }

  const tabs = [
    { id: "quotes", label: "Quotes", icon: FileText },
    { id: "invoices", label: "Invoices", icon: Receipt },
    { id: "aging", label: "Aging", icon: BarChart3 },
    { id: "revenue", label: "Revenue", icon: Package },
    { id: "po-summary", label: "Purchase Orders", icon: Truck },
    { id: "supplier-perf", label: "Supplier Perf.", icon: TrendingUp },
    { id: "deliveries", label: "Deliveries", icon: Clock },
    { id: "expenses", label: "Expenses", icon: Banknote },
    { id: "qr-summary", label: "Quote Requests", icon: FileSpreadsheet },
  ];

  return (
    <div className="animate-fade-in" data-testid="reports-page">
      <h1 className="font-heading font-bold text-2xl text-[#1F2937] tracking-tight mb-1">Reports</h1>
      <p className="text-sm text-gray-500 mb-5">Financial, sales, and procurement reporting</p>

      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`report-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${tab === t.id ? "border-[#E63946] text-[#E63946]" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "quotes" && <QuotesSummary />}
      {tab === "invoices" && <InvoicesSummary />}
      {tab === "aging" && <AgingReport />}
      {tab === "revenue" && <RevenueByProduct />}
      {tab === "po-summary" && <POSummary />}
      {tab === "supplier-perf" && <SupplierPerformance />}
      {tab === "deliveries" && <ExpectedDeliveries />}
      {tab === "expenses" && <ExpensesSummary />}
      {tab === "qr-summary" && <QRSummaryReport />}
    </div>
  );
}

function QuotesSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetch = () => {
    setLoading(true);
    api.get("/reports/quotes-summary", { params: { date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data)).catch(() => toast.error("Failed to load report")).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, []);

  const handleExport = () => {
    if (!data?.quotes?.length) return;
    const headers = "Quote#,Customer,Date,Valid Until,Total,Status\n";
    const rows = data.quotes.map((q) => `${q.quote_number},${q.customer_name},${q.created_at?.split("T")[0]},${q.valid_until},${q.total},${q.status}`).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "quotes_report.csv"; a.click();
    toast.success("Report exported");
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!data) return null;

  const statusData = Object.entries(data.by_status || {}).map(([k, v]) => ({ name: k, count: v.count, value: v.value }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-xs font-medium text-gray-500 mb-1">From</label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <div><label className="block text-xs font-medium text-gray-500 mb-1">To</label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <Button size="sm" onClick={fetch} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm h-9">Apply</Button>
        <Button size="sm" variant="outline" onClick={handleExport} className="rounded-sm h-9"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Quotes</p><p className="font-heading font-bold text-2xl">{data.total_quotes}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Value</p><p className="font-heading font-bold text-2xl">{formatCurrency(data.total_value)}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Conversion Rate</p><p className="font-heading font-bold text-2xl text-purple-600">{data.conversion_rate}%</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Avg Value</p><p className="font-heading font-bold text-2xl">{formatCurrency(data.total_quotes > 0 ? data.total_value / data.total_quotes : 0)}</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold text-[#1F2937]">By Status</CardTitle></CardHeader>
          <CardContent className="px-6 pb-6">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={statusData}><CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => formatCurrency(v)} /><Bar dataKey="value" fill="#E63946" radius={[2, 2, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold text-[#1F2937]">Top Customers by Quote Value</CardTitle></CardHeader>
          <CardContent className="px-6 pb-4">
            {(data.top_customers || []).map((c, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <span className="text-sm truncate max-w-[200px]">{c.name}</span>
                <div className="text-right"><span className="text-sm font-medium">{formatCurrency(c.value)}</span><span className="text-xs text-gray-400 ml-2">({c.count})</span></div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function InvoicesSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetch = () => {
    setLoading(true);
    api.get("/reports/invoices-summary", { params: { date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data)).catch(() => toast.error("Failed to load report")).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, []);

  const handleExport = () => {
    if (!data?.invoices?.length) return;
    const headers = "Invoice#,Customer,Date,Due Date,Total,Paid,Balance,Status\n";
    const rows = data.invoices.map((i) => `${i.invoice_number},${i.customer_name},${i.created_at?.split("T")[0]},${i.due_date},${i.total},${i.amount_paid},${i.balance},${i.status}`).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "invoices_report.csv"; a.click();
    toast.success("Report exported");
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-xs font-medium text-gray-500 mb-1">From</label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <div><label className="block text-xs font-medium text-gray-500 mb-1">To</label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <Button size="sm" onClick={fetch} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm h-9">Apply</Button>
        <Button size="sm" variant="outline" onClick={handleExport} className="rounded-sm h-9"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Invoiced</p><p className="font-heading font-bold text-2xl">{formatCurrency(data.total_invoiced)}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Paid</p><p className="font-heading font-bold text-2xl text-green-600">{formatCurrency(data.total_paid)}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Outstanding</p><p className="font-heading font-bold text-2xl text-orange-600">{formatCurrency(data.outstanding)}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Invoices</p><p className="font-heading font-bold text-2xl">{data.total_invoices}</p></CardContent></Card>
      </div>

      <Card className="border border-gray-200 rounded-sm shadow-sm">
        <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold text-[#1F2937]">Top Customers by Revenue</CardTitle></CardHeader>
        <CardContent className="px-6 pb-4">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200"><th className="text-left py-2 text-xs text-gray-500 uppercase">Customer</th><th className="text-right py-2 text-xs text-gray-500 uppercase">Invoiced</th><th className="text-right py-2 text-xs text-gray-500 uppercase">Paid</th><th className="text-right py-2 text-xs text-gray-500 uppercase">Outstanding</th></tr></thead>
            <tbody>{(data.top_customers || []).map((c, i) => (
              <tr key={i} className="border-b border-gray-100"><td className="py-2 truncate max-w-[200px]">{c.name}</td><td className="py-2 text-right">{formatCurrency(c.invoiced)}</td><td className="py-2 text-right text-green-600">{formatCurrency(c.paid)}</td><td className="py-2 text-right text-orange-600">{formatCurrency(c.outstanding)}</td></tr>
            ))}</tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function AgingReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandBucket, setExpandBucket] = useState(null);

  useEffect(() => {
    api.get("/reports/aging").then((r) => setData(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!data) return null;

  const buckets = [
    { key: "current", label: "Current (0-30 days)", color: "#10B981", bg: "bg-green-50", text: "text-green-700" },
    { key: "days_31_60", label: "31-60 days", color: "#F59E0B", bg: "bg-yellow-50", text: "text-yellow-700" },
    { key: "days_61_90", label: "61-90 days", color: "#F97316", bg: "bg-orange-50", text: "text-orange-700" },
    { key: "days_90_plus", label: "90+ days", color: "#E63946", bg: "bg-red-50", text: "text-red-700" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {buckets.map((b) => (
          <Card key={b.key} className={`border border-gray-200 rounded-sm cursor-pointer hover:shadow-md transition-shadow ${expandBucket === b.key ? "ring-2 ring-[#E63946]" : ""}`} onClick={() => setExpandBucket(expandBucket === b.key ? null : b.key)}>
            <CardContent className="p-4 text-center">
              <p className={`text-xs font-medium uppercase ${b.text}`}>{b.label}</p>
              <p className={`font-heading font-bold text-2xl ${b.text} mt-1`}>{formatCurrency(data[b.key]?.value || 0)}</p>
              <p className="text-xs text-gray-400 mt-0.5">{data[b.key]?.count || 0} invoices</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {expandBucket && data[expandBucket]?.invoices?.length > 0 && (
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Invoice #</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Customer</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Due Date</th>
                <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Days Outstanding</th>
                <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Balance</th>
              </tr></thead>
              <tbody>{data[expandBucket].invoices.map((inv) => (
                <tr key={inv.invoice_id} className="border-b border-gray-100">
                  <td className="px-4 py-2.5 font-mono text-[#E63946]">{inv.invoice_number}</td>
                  <td className="px-4 py-2.5 truncate max-w-[200px]">{inv.customer_name}</td>
                  <td className="px-4 py-2.5 text-gray-600">{inv.due_date}</td>
                  <td className="px-4 py-2.5 text-right">{inv.days_outstanding}</td>
                  <td className="px-4 py-2.5 text-right font-medium">{formatCurrency(inv.balance)}</td>
                </tr>
              ))}</tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function RevenueByProduct() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetch = () => {
    setLoading(true);
    api.get("/reports/revenue-by-product", { params: { date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, []);

  const handleExport = () => {
    if (!data?.products?.length) return;
    const headers = "Product,SKU,Qty Sold,Total Revenue,Avg Price\n";
    const rows = data.products.map((p) => `"${p.description}",${p.sku},${p.qty_sold},${p.total_revenue},${p.avg_price}`).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "revenue_by_product.csv"; a.click();
    toast.success("Report exported");
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!data) return null;

  const chartData = (data.products || []).slice(0, 10);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-xs font-medium text-gray-500 mb-1">From</label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <div><label className="block text-xs font-medium text-gray-500 mb-1">To</label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <Button size="sm" onClick={fetch} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm h-9">Apply</Button>
        <Button size="sm" variant="outline" onClick={handleExport} className="rounded-sm h-9"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
      </div>

      {chartData.length > 0 && (
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold text-[#1F2937]">Top 10 Products by Revenue</CardTitle></CardHeader>
          <CardContent className="px-6 pb-6">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" /><XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} /><YAxis type="category" dataKey="description" width={150} tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => formatCurrency(v)} /><Bar dataKey="total_revenue" fill="#E63946" radius={[0, 2, 2, 0]} /></BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card className="border border-gray-200 rounded-sm shadow-sm">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Product</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">SKU</th>
              <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Qty Sold</th>
              <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Total Revenue</th>
              <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Avg Price</th>
            </tr></thead>
            <tbody>{(data.products || []).length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-gray-400">No revenue data</td></tr>
            ) : (data.products || []).map((p, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td className="px-4 py-2.5 font-medium truncate max-w-[200px]">{p.description}</td>
                <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">{p.sku || "-"}</td>
                <td className="px-4 py-2.5 text-right">{p.qty_sold}</td>
                <td className="px-4 py-2.5 text-right font-medium">{formatCurrency(p.total_revenue)}</td>
                <td className="px-4 py-2.5 text-right">{formatCurrency(p.avg_price)}</td>
              </tr>
            ))}</tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}


function POSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const navigate = useNavigate();

  const fetch = () => {
    setLoading(true);
    api.get("/reports/po-summary", { params: { date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data)).catch(() => toast.error("Failed")).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, []);

  const handleExport = () => {
    if (!data?.by_supplier?.length) return;
    const headers = "Supplier,PO Count,Total Value\n";
    const rows = data.by_supplier.map((s) => `"${s.supplier}",${s.count},${s.value}`).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "po_summary.csv"; a.click();
    toast.success("Exported");
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!data) return null;

  const statusData = Object.entries(data.by_status || {}).map(([k, v]) => ({ name: k, value: v }));
  const statusColors2 = { Draft: "#6B7280", Sent: "#3B82F6", "Partially Fulfilled": "#F59E0B", Fulfilled: "#10B981", Cancelled: "#E63946" };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-xs font-medium text-gray-500 mb-1">From</label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <div><label className="block text-xs font-medium text-gray-500 mb-1">To</label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <Button size="sm" onClick={fetch} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm h-9">Apply</Button>
        <Button size="sm" variant="outline" onClick={handleExport} className="rounded-sm h-9"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total POs</p><p className="font-heading font-bold text-2xl">{data.total_pos}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Value</p><p className="font-heading font-bold text-2xl text-[#E63946]">{formatCurrency(data.total_value)}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Avg PO Value</p><p className="font-heading font-bold text-2xl">{formatCurrency(data.avg_value)}</p></CardContent></Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {statusData.length > 0 && (
          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold">By Status</CardTitle></CardHeader>
            <CardContent className="px-6 pb-6">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart><Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, value }) => `${name}: ${value}`}>
                  {statusData.map((e, i) => <Cell key={i} fill={statusColors2[e.name] || PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie><Tooltip /><Legend /></PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold">By Supplier</CardTitle></CardHeader>
          <CardContent className="px-6 pb-4">
            {(data.by_supplier || []).length === 0 ? <p className="text-sm text-gray-400 py-4 text-center">No data</p> :
              data.by_supplier.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-sm truncate max-w-[200px]">{s.supplier}</span>
                  <div className="text-right"><span className="text-sm font-medium">{formatCurrency(s.value)}</span><span className="text-xs text-gray-400 ml-2">({s.count} POs)</span></div>
                </div>
              ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SupplierPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetch = () => {
    setLoading(true);
    api.get("/reports/supplier-performance", { params: { date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data)).catch(() => toast.error("Failed")).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, []);

  const handleExport = () => {
    if (!data?.suppliers?.length) return;
    const headers = "Supplier,PO Count,Total Value,Avg Delivery Days,On-Time %\n";
    const rows = data.suppliers.map((s) => `"${s.supplier_name}",${s.po_count},${s.total_value},${s.avg_delivery_days},${s.on_time_pct}%`).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "supplier_performance.csv"; a.click();
    toast.success("Exported");
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-xs font-medium text-gray-500 mb-1">From</label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <div><label className="block text-xs font-medium text-gray-500 mb-1">To</label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <Button size="sm" onClick={fetch} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm h-9">Apply</Button>
        <Button size="sm" variant="outline" onClick={handleExport} className="rounded-sm h-9"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
      </div>
      <Card className="border border-gray-200 rounded-sm shadow-sm">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Supplier</th>
              <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">PO Count</th>
              <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Total Value</th>
              <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Avg Delivery</th>
              <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">On-Time %</th>
            </tr></thead>
            <tbody>
              {(data.suppliers || []).length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-gray-400">No supplier data</td></tr>
              ) : data.suppliers.map((s, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="px-4 py-2.5 font-medium">{s.supplier_name}</td>
                  <td className="px-4 py-2.5 text-right">{s.po_count}</td>
                  <td className="px-4 py-2.5 text-right font-medium">{formatCurrency(s.total_value)}</td>
                  <td className="px-4 py-2.5 text-right">{s.avg_delivery_days > 0 ? `${s.avg_delivery_days} days` : "—"}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={`font-medium ${s.on_time_pct >= 80 ? "text-green-600" : s.on_time_pct >= 50 ? "text-amber-600" : "text-red-600"}`}>
                      {s.on_time_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function ExpectedDeliveries() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/reports/expected-deliveries").then((r) => setData(r.data)).catch(() => toast.error("Failed")).finally(() => setLoading(false));
  }, []);

  const handleExport = () => {
    if (!data?.deliveries?.length) return;
    const headers = "PO#,Supplier,Expected Date,Status,Overdue\n";
    const rows = data.deliveries.map((d) => `${d.po_number},"${d.supplier_name}",${d.expected_delivery_date},${d.status},${d.is_overdue ? "Yes" : "No"}`).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "expected_deliveries.csv"; a.click();
    toast.success("Exported");
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!data) return null;

  const overdueCount = (data.deliveries || []).filter((d) => d.is_overdue).length;
  const upcomingCount = (data.deliveries || []).filter((d) => d.is_upcoming).length;

  return (
    <div className="space-y-6">
      <div className="flex items-end gap-3">
        <Button size="sm" variant="outline" onClick={handleExport} className="rounded-sm h-9"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
        <div className="flex gap-2 ml-auto">
          {overdueCount > 0 && <Badge className="bg-red-50 text-red-600 border-red-200 rounded-full">{overdueCount} Overdue</Badge>}
          {upcomingCount > 0 && <Badge className="bg-green-50 text-green-600 border-green-200 rounded-full">{upcomingCount} This Week</Badge>}
        </div>
      </div>
      <Card className="border border-gray-200 rounded-sm shadow-sm">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">PO #</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Supplier</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Expected Date</th>
              <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Total</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Alert</th>
            </tr></thead>
            <tbody>
              {(data.deliveries || []).length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400">No pending deliveries</td></tr>
              ) : data.deliveries.map((d) => (
                <tr key={d.po_id} className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${d.is_overdue ? "bg-red-50/30" : d.is_upcoming ? "bg-green-50/20" : ""}`} onClick={() => navigate(`/purchase-orders/${d.po_id}`)}>
                  <td className="px-4 py-2.5 font-mono font-semibold text-[#E63946]">{d.po_number}</td>
                  <td className="px-4 py-2.5">{d.supplier_name}</td>
                  <td className="px-4 py-2.5">{formatDate(d.expected_delivery_date)}</td>
                  <td className="px-4 py-2.5 text-right font-medium">{formatCurrency(d.total)}</td>
                  <td className="px-4 py-2.5"><Badge className={`text-xs rounded-full ${d.status === "Sent" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>{d.status}</Badge></td>
                  <td className="px-4 py-2.5">
                    {d.is_overdue && <Badge className="bg-red-100 text-red-700 text-xs rounded-full"><AlertTriangle className="w-3 h-3 mr-0.5 inline" /> Overdue</Badge>}
                    {d.is_upcoming && !d.is_overdue && <Badge className="bg-green-100 text-green-700 text-xs rounded-full">This Week</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function ExpensesSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetch = () => {
    setLoading(true);
    api.get("/reports/expenses-summary", { params: { date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data)).catch(() => toast.error("Failed")).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, []);

  const handleExport = () => {
    if (!data?.expenses?.length) return;
    const headers = "Expense#,Vendor,Category,Date,Amount,Status\n";
    const rows = data.expenses.map((e) => `${e.expense_number},"${e.vendor}","${e.category_name}",${e.expense_date},${e.amount},${e.status}`).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "expenses_report.csv"; a.click();
    toast.success("Exported");
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!data) return null;

  const catData = (data.by_category || []).map((c, i) => ({ ...c, name: c.category }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-xs font-medium text-gray-500 mb-1">From</label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <div><label className="block text-xs font-medium text-gray-500 mb-1">To</label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <Button size="sm" onClick={fetch} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm h-9">Apply</Button>
        <Button size="sm" variant="outline" onClick={handleExport} className="rounded-sm h-9"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Expenses</p><p className="font-heading font-bold text-2xl">{data.total_expenses}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total Amount</p><p className="font-heading font-bold text-2xl text-[#E63946]">{formatCurrency(data.total_amount)}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Approved</p><p className="font-heading font-bold text-2xl text-green-600">{formatCurrency(data.approved_total)}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Pending</p><p className="font-heading font-bold text-2xl text-amber-600">{formatCurrency(data.pending_total)}</p></CardContent></Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {catData.length > 0 && (
          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold">By Category</CardTitle></CardHeader>
            <CardContent className="px-6 pb-6">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={catData}><CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" /><XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} /><YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} /><Tooltip formatter={(v) => formatCurrency(v)} /><Bar dataKey="amount" fill="#E63946" radius={[2, 2, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold">By User</CardTitle></CardHeader>
          <CardContent className="px-6 pb-4">
            {(data.by_user || []).length === 0 ? <p className="text-sm text-gray-400 py-4 text-center">No data</p> :
              data.by_user.map((u, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-sm">{u.user}</span>
                  <div className="text-right"><span className="text-sm font-medium">{formatCurrency(u.amount)}</span><span className="text-xs text-gray-400 ml-2">({u.count})</span></div>
                </div>
              ))}
          </CardContent>
        </Card>
      </div>
      {(data.monthly_trend || []).length > 1 && (
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold">Monthly Trend</CardTitle></CardHeader>
          <CardContent className="px-6 pb-6">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.monthly_trend}><CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} /><Tooltip formatter={(v) => formatCurrency(v)} /><Bar dataKey="amount" fill="#424242" radius={[2, 2, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
function QRSummaryReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const navigate = useNavigate();

  const fetch = () => {
    setLoading(true);
    api.get("/reports/qr-summary", { params: { date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data)).catch(() => toast.error("Failed to load report")).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, []);

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!data) return null;

  const statusData = Object.entries(data.by_status || {}).map(([k, v]) => ({ name: k, count: v }));

  return (
    <div data-testid="qr-summary-report" className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-xs font-medium text-gray-500 mb-1">From</label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <div><label className="block text-xs font-medium text-gray-500 mb-1">To</label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm border-gray-300 h-9 w-40" /></div>
        <Button size="sm" onClick={fetch} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm h-9">Apply</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Total QRs</p><p className="font-heading font-bold text-2xl">{data.total_qrs}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Avg Response</p><p className="font-heading font-bold text-2xl text-blue-600">{data.avg_response_days} days</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Conversion Rate</p><p className="font-heading font-bold text-2xl text-purple-600">{data.conversion_rate}%</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">POs Created</p><p className="font-heading font-bold text-2xl text-green-600">{data.po_created}</p></CardContent></Card>
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase mb-1">Draft</p><p className="font-heading font-bold text-2xl text-gray-500">{data.by_status?.Draft || 0}</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold text-[#1F2937]">QRs by Status</CardTitle></CardHeader>
          <CardContent className="px-6 pb-6">
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={statusData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, count }) => `${name}: ${count}`} labelLine={false}>
                    {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">No data</div>}
          </CardContent>
        </Card>
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold text-[#1F2937]">Supplier Response Performance</CardTitle></CardHeader>
          <CardContent className="px-6 pb-4">
            {(data.by_supplier || []).length === 0 ? (
              <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">No supplier data</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-xs text-gray-500 uppercase">Supplier</th>
                  <th className="text-center py-2 text-xs text-gray-500 uppercase">Sent</th>
                  <th className="text-center py-2 text-xs text-gray-500 uppercase">Received</th>
                  <th className="text-center py-2 text-xs text-gray-500 uppercase">Avg Days</th>
                  <th className="text-center py-2 text-xs text-gray-500 uppercase">Rate</th>
                </tr></thead>
                <tbody>{data.by_supplier.map((s, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-2 truncate max-w-[160px]">{s.supplier}</td>
                    <td className="py-2 text-center">{s.sent}</td>
                    <td className="py-2 text-center text-green-600 font-medium">{s.received}</td>
                    <td className="py-2 text-center">{s.avg_response_days || "—"}</td>
                    <td className="py-2 text-center"><Badge className={`text-xs rounded-full ${s.response_rate >= 80 ? "bg-green-50 text-green-700" : s.response_rate >= 50 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600"}`}>{s.response_rate}%</Badge></td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

